/**
 * One place to hand a failure to Sentry.
 *
 * Everything in this app that can fail already fails politely — a readable
 * message, a state reset, and on with your day. That is right for the person
 * using it and useless for anyone fixing it: a writer whose recording won't
 * save sees a sentence and we see nothing at all. This closes that gap without
 * changing what the user experiences.
 *
 * Rules this file exists to hold in one spot:
 *
 * - It never throws. A reporting failure must not become the failure. Every
 *   call site treats this as fire-and-forget, so a bug in here would be the
 *   worst possible bug to have.
 * - It never sends song content, lyrics, audio, or anything typed into a form.
 *   Sentry is configured with autocapture off for the same reason; this keeps
 *   that promise at the call sites too. Pass ids and states, never text.
 * - It is a no-op when VITE_SENTRY_DSN is unset, which is every local dev
 *   session. Sentry.init() is skipped there, so captureException would be a
 *   silent nothing anyway — this just makes that explicit.
 */
import * as Sentry from '@sentry/react'

/**
 * @param {unknown} err    the thrown thing, whatever it turned out to be
 * @param {string}  area   the subsystem, used as the Sentry `area` tag — keep
 *                         the set small so the dashboard groups usefully
 * @param {object}  [context] ids and states only. No user-authored text.
 */
export function report(err, area, context) {
  try {
    Sentry.captureException(err, {
      tags: { area },
      // `extra` rather than `contexts` so a flat bag of ids shows up as its own
      // readable block on the issue, instead of being flattened into the tags
      // list and cluttering the search facets.
      extra: context || undefined,
    })
  } catch {
    // Sentry not initialised, or an exotic error object it can't serialise.
    // Nothing here is worth breaking a user's action over.
  }
}

/**
 * The same thing for a failure the user never sees — a background call that
 * was deliberately fire-and-forget. Tagged so these can be triaged separately
 * from failures someone was actually waiting on.
 */
export function reportSilent(err, area, context) {
  report(err, area, { ...(context || {}), silent: true })
}
